import {
  createFinalMarkdownCard,
} from '@/lib/idea-card-parser-output';
import type {
  IdeaCardParserLineRange,
  IdeaCardParserPhase,
} from '@/lib/idea-card-parser-phases';

interface HeadingSection {
  title: string;
  start: number;
  bodyLines: string[];
}

const stripMd = (value: string) => value.replace(/^\s*[\*\_\-\`~]+|[\*\_\-\`~]+\s*$/g, '').trim();

const headingOnlyRe = /^\s*([^:\uFF1A\n]{2,100})[:\uFF1A]\s*$/;
const headingInlineRe = /^\s*([^:\uFF1A\n]{2,100})[:\uFF1A]\s*(.+)$/;

const isIndexedHeading = (title: string) => /^[^\s:]+(?:\s+[^\s:]+){0,2}\s+\d+\s*$/.test(title.trim());

export const parseHeadingSectionPhase: IdeaCardParserPhase = context => {
  const usedRanges: IdeaCardParserLineRange[] = [];
  const sections: HeadingSection[] = [];
  let current: HeadingSection | null = null;
  const { lines, isPartOfTable } = context;

  const finalizeCurrentSection = (endLine: number) => {
    if (!current) return;

    usedRanges.push({ start: current.start, end: endLine });
    sections.push(current);
  };

  for (let index = 0; index < lines.length; index++) {
    const raw = lines[index];
    const line = raw.trimEnd();

    if (isPartOfTable[index]) {
      if (current) current.bodyLines.push(line);
      continue;
    }

    const markdownHeadingMatch = line.match(/^\s*#+\s+(.*)/);
    if (markdownHeadingMatch) {
      const candidateTitle = markdownHeadingMatch[1].trim();
      if (current && isIndexedHeading(current.title) && !isIndexedHeading(candidateTitle)) {
        current.bodyLines.push(line);
        continue;
      }

      finalizeCurrentSection(index - 1);
      current = { title: candidateTitle.slice(0, 100), start: index, bodyLines: [] };
      continue;
    }

    const urlHeaderMatch = line.match(/^\s*((?:(?:\d+)[\.\)]\s*)?https?:\/\/[^\s]+)(.*)$/i);
    if (urlHeaderMatch) {
      if (current) current.bodyLines.push(line);
      continue;
    }

    const cleaned = stripMd(line);
    const inlineMatch = cleaned.match(headingInlineRe);
    const titleOnlyMatch = cleaned.match(headingOnlyRe);
    const startsWithQuoteOrBullet = /^["'“”\-\*•\d]/.test(raw.trim());
    const inlineTitle = inlineMatch?.[1] || '';
    const inlineTitleHasSpace = /\s/.test(inlineTitle);

    if (!startsWithQuoteOrBullet && /—/.test(cleaned) && !/:\s*/.test(cleaned)) {
      if (current) {
        current.bodyLines.push(line);
        continue;
      }

      current = { title: cleaned.slice(0, 100), start: index, bodyLines: [] };
      continue;
    }

    if (!startsWithQuoteOrBullet && isIndexedHeading(cleaned) && cleaned.length <= 100) {
      finalizeCurrentSection(index - 1);
      current = { title: cleaned.trim(), start: index, bodyLines: [] };
      continue;
    }

    const isTimelineLabel =
      inlineMatch &&
      /^(?:[A-Za-z]\s*[-+]?\d+(?:\s*[\/–—-]\s*[A-Za-z]?[-+]?\d+)?|[A-Za-z][+-]?\d+)\s*$/.test(inlineTitle);
    const isNumberedSubLabel =
      inlineMatch && /^[^\d]{1,24}\s+\d+(?:\s*[–-]\s*\d+)?\s*$/i.test(inlineTitle);
    const isNumberedSubLabelOnly =
      titleOnlyMatch &&
      /^[^\d]{1,24}\s+\d+(?:\s*[–-]\s*\d+)?\s*$/i.test(stripMd(titleOnlyMatch[1] || '').trim());
    const isShotListSubsection = inlineMatch && /\(\d+[–\-]\d+s\)/.test(inlineTitle);
    const isShotListSubsectionTitleOnly =
      titleOnlyMatch && /\(\d+[–\-]\d+s\)/.test(titleOnlyMatch[1]);

    const hasIndentation = /^\s/.test(raw);
    const isBulleted = /^["'“”\-\*•]/.test(raw.trim());

    if (inlineMatch && inlineTitleHasSpace) {
      if (current && (hasIndentation || isBulleted || isTimelineLabel || isNumberedSubLabel || isShotListSubsection)) {
        current.bodyLines.push(line);
        continue;
      }

      const candidateTitle = stripMd(inlineMatch[1]).slice(0, 100).trim();
      if (current && isIndexedHeading(current.title) && !isIndexedHeading(candidateTitle)) {
        current.bodyLines.push(line);
        continue;
      }

      finalizeCurrentSection(index - 1);
      current = {
        title: candidateTitle,
        start: index,
        bodyLines: inlineMatch[2].trim() ? [inlineMatch[2].trim()] : [],
      };
      continue;
    }

    if (titleOnlyMatch) {
      if (current && (hasIndentation || isBulleted || isShotListSubsectionTitleOnly || isNumberedSubLabelOnly)) {
        current.bodyLines.push(line);
        continue;
      }

      const candidateTitleOnly = stripMd(titleOnlyMatch[1]).slice(0, 100).trim();
      if (current && isIndexedHeading(current.title) && !isIndexedHeading(candidateTitleOnly)) {
        current.bodyLines.push(line);
        continue;
      }

      finalizeCurrentSection(index - 1);
      current = { title: candidateTitleOnly, start: index, bodyLines: [] };
      continue;
    }

    if (current) current.bodyLines.push(line);
  }

  if (current) {
    usedRanges.push({ start: current.start, end: lines.length - 1 });
    sections.push(current);
  }

  if (sections.length === 0) return null;

  // Filter sections and ranges together to keep them in sync
  const validSections = sections
    .map((section, index) => ({
      section,
      range: usedRanges[index],
      card: createFinalMarkdownCard({
        title: section.title,
        body: section.bodyLines.join('\n').trim() || undefined,
      }),
    }))
    .filter(({ card }) => card.title && !!(card.body && card.body.length > 0));

  if (validSections.length === 0) return null;

  return {
    cards: validSections.map(({ card }) => card),
    usedRanges: validSections.map(({ range }) => range),
  };
};
